import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Button,
  Dialog,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import api, { handleCatch } from '../api';
import MobileHeader from '../components/MobileHeader';
import { formatInBusinessTimezone } from '../utils/dateTimeHelper';

const PhotoGalleryScreen = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);

  useEffect(() => {
    loadData(true);
  }, []);

  const loadData = async (isInitial = false) => {
    try {
      if (isInitial) {
        setLoading(true);
        setPageNumber(1);
        setHasMoreData(true);
      }
      const currentPage = isInitial ? 1 : pageNumber;
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `/api/school-common/photo-gallery/get-photo-student?PageSize=10&PageNumber=${currentPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const newData = response.data.data || [];
      const transformedData = newData.map((item, index) => ({
        id: item.id || `${currentPage}_${index}`,
        title: item.title || 'Untitled',
        imageString: item.imageUrl || item.imageString,
        uploadedDate: item.creationDate || item.uploadedDate,
        album: item.categoryName || item.category || 'General',
      }));
      if (isInitial) {
        setItems(transformedData);
      } else {
        setItems((prev) => [...prev, ...transformedData]);
      }
      if (newData.length === 10) {
        setPageNumber((prev) => prev + 1);
      } else {
        setHasMoreData(false);
      }
    } catch (error) {
      console.error('Error loading photo gallery:', error);
      handleCatch(error);
      if (isInitial) setItems([]);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const openImageModal = (imageItem) => {
    setSelectedImage(imageItem);
    setModalVisible(true);
  };

  const closeImageModal = () => {
    setModalVisible(false);
    setSelectedImage(null);
  };

  const onRefresh = async () => {
    await loadData(true);
  };

  const loadMoreData = async () => {
    if (hasMoreData && !loading) {
      setLoading(true);
      await loadData(false);
    }
  };

  const groupedAlbums = useMemo(() => {
    const grouped = items.reduce((acc, item) => {
      const albumName = item.album || 'General';
      if (!acc[albumName]) {
        acc[albumName] = [];
      }
      acc[albumName].push(item);
      return acc;
    }, {});

    return Object.entries(grouped).map(([album, photos]) => ({
      album,
      photos,
    }));
  }, [items]);

  const selectedAlbumPhotos = useMemo(() => {
    if (!selectedAlbum) return [];
    const album = groupedAlbums.find((entry) => entry.album === selectedAlbum);
    return album ? album.photos : [];
  }, [groupedAlbums, selectedAlbum]);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F5F7FA' }}>
      <MobileHeader
        title="Photo Gallery"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Box sx={{ flex: 1, overflowY: 'auto', p: 0.5, pb: 2 }}>
        {initialLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 10 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: 14 }}>
              Loading gallery...
            </Typography>
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 10, px: 3 }}>
            <Typography sx={{ fontSize: 64, mb: 1.5 }}></Typography>
            <Typography sx={{ fontWeight: 'bold', fontSize: 17, mb: 1, textAlign: 'center' }}>
              No Photos Yet
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center', mb: 3 }}>
              School photos will appear here when available.
            </Typography>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={onRefresh}>
              Refresh
            </Button>
          </Box>
        ) : !selectedAlbum ? (
          <>
            <Typography sx={{ px: 1, py: 1, fontSize: 14, color: 'text.secondary' }}>
              Select an album
            </Typography>
            {groupedAlbums.map((albumGroup) => (
              <Box
                key={albumGroup.album}
                onClick={() => setSelectedAlbum(albumGroup.album)}
                sx={{
                  mx: 1,
                  mb: 1,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: '#fff',
                  border: '1px solid #E5E7EB',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ flex: 1, pr: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                    {albumGroup.album}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                    {albumGroup.photos.length} photos
                  </Typography>
                </Box>
                <Box sx={{ width: 56, height: 56, position: 'relative' }}>
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gridTemplateRows: 'repeat(2, 1fr)',
                      gap: '2px',
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: '#E5E7EB',
                    }}
                  >
                    {[0, 1, 2, 3].map((idx) => (
                      <Box
                        key={idx}
                        component="img"
                        src={albumGroup.photos[idx]?.imageString}
                        alt={albumGroup.photos[idx]?.title || albumGroup.album}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: albumGroup.photos[idx]?.imageString ? 'block' : 'none',
                        }}
                      />
                    ))}
                  </Box>
                  {albumGroup.photos[0]?.imageString && (
                    <Box
                      component="img"
                      src={albumGroup.photos[0].imageString}
                      alt={albumGroup.photos[0].title || albumGroup.album}
                      sx={{
                        position: 'absolute',
                        width: 26,
                        height: 26,
                        bottom: -4,
                        right: -4,
                        borderRadius: 0.8,
                        objectFit: 'cover',
                        border: '2px solid #fff',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                      }}
                    />
                  )}
                </Box>
              </Box>
            ))}
            {hasMoreData && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
                <Button
                  variant="outlined"
                  onClick={loadMoreData}
                  disabled={loading}
                  sx={{ borderRadius: 20, px: 4 }}
                >
                  {loading ? <CircularProgress size={22} /> : 'Load More Categories'}
                </Button>
              </Box>
            )}
          </>
        ) : (
          <>
            <Box sx={{ px: 1, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button size="small" variant="outlined" onClick={() => setSelectedAlbum(null)}>
                Back
              </Button>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1F2937' }}>
                {selectedAlbum}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '2px',
              }}
            >
              {selectedAlbumPhotos.map((item) => (
                <Box
                  key={item.id}
                  onClick={() => openImageModal(item)}
                  sx={{
                    position: 'relative',
                    paddingTop: '100%',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    bgcolor: '#e0e0e0',
                  }}
                >
                  <Box
                    component="img"
                    src={item.imageString}
                    alt={item.title}
                    loading="lazy"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)',
                      px: 0.5,
                      py: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#fff',
                        fontSize: 9,
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>

      {/* Image Modal */}
      <Dialog
        open={modalVisible}
        onClose={closeImageModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { bgcolor: 'rgba(0,0,0,0.95)' },
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={closeImageModal}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: '#fff',
              zIndex: 1,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>

          {selectedImage && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '60vh',
                  maxHeight: '80vh',
                  p: 3,
                }}
              >
                <Box
                  component="img"
                  src={selectedImage.imageString}
                  alt={selectedImage.title}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: 1,
                  }}
                />
              </Box>
              <Box sx={{ bgcolor: 'rgba(0,0,0,0.7)', px: 3, py: 2 }}>
                <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: 15, mb: 0.5 }}>
                  {selectedImage.title}
                </Typography>
                <Typography sx={{ color: '#bbb', fontSize: 12 }}>
                   {selectedImage.album} &nbsp;&nbsp; {' '}
                  {formatInBusinessTimezone(selectedImage.uploadedDate, 'dd MMM yyyy')}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default PhotoGalleryScreen;
